/**
 * Botões do chatbot com links clicáveis (espelha uraButtonLinks.js na API).
 */

import { WHATSAPP_REPLY_BUTTON_TITLE_MAX } from "../components/ChatbotFlow/chatbotWhatsAppLimits";

const LINK_SEPARATORS = ["|", "::"];

export function isHttpUrl(value) {
  const text = String(value || "").trim();
  return /^https?:\/\//i.test(text);
}

export function defaultLinkLabel(url) {
  try {
    const host = new URL(url).hostname.replace(/^www\./i, "");
    const label = host || "Abrir link";
    return label.length > WHATSAPP_REPLY_BUTTON_TITLE_MAX
      ? label.slice(0, WHATSAPP_REPLY_BUTTON_TITLE_MAX)
      : label;
  } catch (_) {
    return "Abrir link";
  }
}

/**
 * @param {*} raw
 * @returns {{ kind: "reply"|"link", label: string, url?: string, raw: string, flowKey: string }}
 */
export function parseChatbotButtonEntry(raw) {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const url = String(raw.url || raw.href || raw.link || "").trim();
    const explicitType = String(raw.type || raw.kind || "").toLowerCase();
    const label = String(raw.label || raw.title || raw.text || raw.name || "").trim();

    if ((explicitType === "link" || url) && isHttpUrl(url)) {
      const display = clipLabel(label || defaultLinkLabel(url));
      return {
        kind: "link",
        label: display,
        url,
        raw: label ? `${label}|${url}` : url,
        flowKey: display,
      };
    }

    const replyLabel = label;
    const replyId = String(raw.id || raw.payload || raw.value || "").trim();
    return {
      kind: "reply",
      label: replyLabel,
      id: replyId,
      raw: replyLabel,
      flowKey: replyLabel,
    };
  }

  const text = String(raw ?? "").trim();
  if (!text) return { kind: "reply", label: "", raw: "", flowKey: "" };

  if (isHttpUrl(text)) {
    const display = defaultLinkLabel(text);
    return { kind: "link", label: display, url: text, raw: text, flowKey: display };
  }

  for (const sep of LINK_SEPARATORS) {
    const idx = text.indexOf(sep);
    if (idx > 0) {
      const labelPart = text.slice(0, idx).trim();
      const urlPart = text.slice(idx + sep.length).trim();
      if (isHttpUrl(urlPart)) {
        const display = clipLabel(labelPart || defaultLinkLabel(urlPart));
        return {
          kind: "link",
          label: display,
          url: urlPart,
          raw: text,
          flowKey: labelPart || defaultLinkLabel(urlPart),
        };
      }
    }
  }

  return { kind: "reply", label: text, raw: text, flowKey: text };
}

function clipLabel(label) {
  const text = String(label || "").trim();
  if (!text) return "";
  return text.length > WHATSAPP_REPLY_BUTTON_TITLE_MAX
    ? text.slice(0, WHATSAPP_REPLY_BUTTON_TITLE_MAX)
    : text;
}

export function parseChatbotButtonEntries(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(parseChatbotButtonEntry).filter((entry) => entry.label || entry.url);
  if (typeof raw === "string" && raw.trim()) {
    if (raw.trim().startsWith("[") && raw.trim().endsWith("]")) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parseChatbotButtonEntries(parsed);
      } catch (_) {
        /* fallthrough */
      }
    }
    return raw
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .map(parseChatbotButtonEntry)
      .filter((entry) => entry.label || entry.url);
  }
  return [];
}

export function serializeChatbotButton(entry) {
  if (!entry || typeof entry !== "object") return "";
  if (entry.kind === "link" && entry.url) {
    const label = clipLabel(entry.label);
    const url = String(entry.url).trim();
    if (!label || label === defaultLinkLabel(url)) return url;
    return `${label}|${url}`;
  }
  return String(entry.label || "").trim();
}

export function splitStoredButton(raw) {
  const parsed = parseChatbotButtonEntry(raw);
  if (parsed.kind === "link") {
    return { label: parsed.label, url: parsed.url || "", isLink: true };
  }
  return { label: parsed.label, url: "", isLink: false };
}

export function isLinkButton(raw) {
  return parseChatbotButtonEntry(raw).kind === "link";
}

export function chatbotButtonFlowKey(raw) {
  return parseChatbotButtonEntry(raw).flowKey;
}

export function partitionChatbotButtonEntries(entries) {
  const replies = [];
  const links = [];
  for (const entry of entries) {
    if (entry.kind === "link" && entry.url) links.push(entry);
    else if (entry.kind === "reply" && entry.label) replies.push(entry);
  }
  return { replies, links };
}

const PAYMENT_URL_HINT_RE =
  /mercadopago|pagarme|pagar\.me|cielo|checkout\/v1|mpago\.|stripe\.com\/pay|paypal\.com\/|asaas\.com|pagseguro|getnet|rede\.com\.br\/checkout/i;

export function extractPaymentCheckoutUrl(text) {
  const matches = String(text || "").match(/https?:\/\/[^\s<>"']+/gi) || [];
  for (const raw of matches) {
    const url = raw.replace(/[)\].,;!?]+$/g, "");
    if (!isHttpUrl(url)) continue;
    if (PAYMENT_URL_HINT_RE.test(url)) return url;
  }
  return "";
}

function stripExactUrlFromBody(body, url) {
  if (!url) return String(body || "");
  const escaped = String(url).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return String(body || "")
    .replace(new RegExp(`\\s*${escaped}\\s*`, "g"), "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Garante botão "Pagar" no simulador a partir de buttonMessage, interactiveOptions
 * ou URL de checkout no texto (paridade com enrich da API).
 */
export function enrichSimulatorPaymentLinkButtons({ text, replies = [], links = [], interactiveOptions } = {}) {
  const safeReplies = Array.isArray(replies) ? replies : [];
  const safeLinks = Array.isArray(links) ? [...links] : [];
  let nextText = String(text || "");

  const ix = interactiveOptions && typeof interactiveOptions === "object" ? interactiveOptions : null;
  const ctaUrl = String(ix?.ctaUrl || ix?.url || "").trim();
  const ctaLabel = String(
    ix?.ctaDisplayText || ix?.ctaLabel || ix?.label || ix?.title || "Pagar"
  ).trim();
  if (ctaUrl && isHttpUrl(ctaUrl) && !safeLinks.some((l) => l.url === ctaUrl)) {
    safeLinks.push({ label: clipLabel(ctaLabel || "Pagar"), url: ctaUrl });
  }

  if (!safeLinks.length && !safeReplies.length) {
    const payUrl = extractPaymentCheckoutUrl(nextText);
    if (payUrl) {
      safeLinks.push({ label: "Pagar", url: payUrl });
      nextText = stripExactUrlFromBody(nextText, payUrl) || nextText;
    }
  }

  return { text: nextText, replies: safeReplies, links: safeLinks };
}
